<template>
  <div v-if="modelValue" class="slider-captcha-popup">
    <div class="captcha-container">
      <div class="captcha-wrapper">
        <div class="captcha-image-container">
          <!-- Loading Spinner -->
          <div v-if="isLoading" class="loading-overlay">
            <van-loading type="spinner" color="#1989fa" />
          </div>

          <!-- Refresh Button -->
          <div class="refresh-icon" @click="refreshCaptcha" v-if="!isLoading">
            <van-icon name="replay" size="20" color="#fff" />
          </div>

          <!-- Background Image -->
          <div class="captcha-image" :style="{ backgroundImage: `url('${captchaData.backgroundImage}')` }">
            <img v-if="captchaData.sliderImage" :src="captchaData.sliderImage" class="puzzle"
              :style="{ left: sliderX + 'px', top: captchaData.targetY + 'px' }" />
          </div>
        </div>

        <div class="slider-bar" :class="verificationStatus">
          <div class="slider-btn" ref="sliderRef" :style="{ left: sliderX + 'px' }" :class="verificationStatus"
            @mousedown="startDrag" @touchstart.prevent="startDragTouch">
            <span v-if="verificationStatus === 'idle'">▶</span>
            <span v-else-if="verificationStatus === 'success'">✔</span>
            <span v-else>✖</span>
          </div>
          <span class="slider-text">{{ $t(statusText) }}</span>
        </div>
      </div>
      <button @click="closePopup" class="close-btn">{{ $t('common.close') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineEmits, defineProps } from 'vue';
import { initCaptcha, verifyCaptcha } from '@/api/captcha';

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(['update:modelValue', 'success']);

const sliderX = ref(0);
const captchaData = ref({
  backgroundImage: '',
  sliderImage: '',
  targetY: 0,
  captchaId: ''
});

const dragging = ref(false);
const isLoading = ref(false);
const verificationStatus = ref('idle'); // idle, success, error
const maxWidth = 208; // 248 (container) - 40 (btn)
let startX = 0;

const statusText = computed(() => {
  if (verificationStatus.value === 'success') return 'common.sliderSuccess';
  if (verificationStatus.value === 'error') return 'common.sliderError';
  return 'common.dragToComplete';
});

watch(() => props.modelValue, (val) => {
  if (val) {
    refreshCaptcha();
  }
});

async function refreshCaptcha() {
  isLoading.value = true;
  sliderX.value = 0;
  verificationStatus.value = 'idle';
  try {
    const res = await initCaptcha();
    if (res.code === 200) {
      captchaData.value = res.data;
    }
  } catch (e) {
    console.error('Captcha Init Failed', e);
  } finally {
    isLoading.value = false;
  }
}

// --- Mouse Drag Handlers ---
const startDrag = (e) => {
  if (verificationStatus.value !== 'idle' || isLoading.value) return;
  e.preventDefault();
  dragging.value = true;
  startX = e.clientX - sliderX.value;
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (e) => {
  if (!dragging.value) return;
  let x = e.clientX - startX;
  sliderX.value = Math.max(0, Math.min(x, maxWidth));
};

const stopDrag = async () => {
  if (!dragging.value) return;
  dragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  await handleVerification();
};

// --- Touch Drag Handlers ---
const startDragTouch = (e) => {
  if (verificationStatus.value !== 'idle' || isLoading.value) return;
  dragging.value = true;
  const touch = e.touches[0];
  startX = touch.clientX - sliderX.value;
  document.addEventListener('touchmove', onDragTouch);
  document.addEventListener('touchend', stopDragTouch);
};

const onDragTouch = (e) => {
  if (!dragging.value) return;
  const touch = e.touches[0];
  let x = touch.clientX - startX;
  sliderX.value = Math.max(0, Math.min(x, maxWidth));
};

const stopDragTouch = async () => {
  if (!dragging.value) return;
  dragging.value = false;
  document.removeEventListener('touchmove', onDragTouch);
  document.removeEventListener('touchend', stopDragTouch);
  await handleVerification();
};



async function handleVerification() {
  try {
    // If your image display size matches backend (248px), 
    // sliderX will map 1:1 to targetX.
    await verifyCaptcha({
      captchaId: captchaData.value.captchaId,
      // Ensure we send an integer
      x: Math.round(sliderX.value)
    });
    
    verificationStatus.value = 'success';
    setTimeout(() => {
      emit('success', { captchaId: captchaData.value.captchaId });
      closePopup();
    }, 1000);
  } catch (err) {
    verificationStatus.value = 'error';
    // Clear slider on error so they have to try again
    setTimeout(() => {
        refreshCaptcha();
    }, 1000);
  }
}

const closePopup = () => emit('update:modelValue', false);
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.slider-captcha-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.captcha-container {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.captcha-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.captcha-image-container {
  position: relative;
  width: 248px;
  height: 140px;
  margin-bottom: 12px;
}

.captcha-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f0f0f0; /* Placeholder color */
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
  border-radius: 6px;
}

.refresh-icon {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 30px;
  height: 30px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 15;
}

.refresh-icon:hover {
  background: rgba(0, 0, 0, 0.6);
}

.puzzle {
  position: absolute;
  width: 40px; /* Adjust based on backend generation */
  height: 40px;
  z-index: 10;
  pointer-events: none;
}

.slider-bar {
  position: relative;
  width: 248px;
  height: 40px;
  background: #eee;
  border-radius: 20px;
}

.slider-btn {
  position: absolute;
  top: 0;
  width: 40px;
  height: 40px;
  background: #409eff;
  color: white;
  text-align: center;
  line-height: 40px;
  border-radius: 50%;
  cursor: grab;
  user-select: none;
  z-index: 2;
  touch-action: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.slider-btn:active {
  cursor: grabbing;
  background: #66b1ff;
}

.slider-btn.success {
  background: #67c23a;
  border-color: #67c23a;
}

.slider-btn.error {
  background: #f56c6c;
  border-color: #f56c6c;
}

.slider-bar.success {
  background: #f0f9eb;
  border: 1px solid #67c23a;
}

.slider-bar.error {
  background: #fef0f0;
  border: 1px solid #f56c6c;
}

.slider-text {
  position: absolute;
  width: 100%;
  text-align: center;
  line-height: 40px;
  font-size: 13px;
  color: #666;
  pointer-events: none;
  user-select: none;
}

.close-btn {
  margin-top: 12px;
  padding: 6px 12px;
  border: none;
  background: #f56c6c;
  color: white;
  border-radius: 4px;
  cursor: pointer;
}
</style>